import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { fileService } from '../services/fileService'

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
}

function StatusCell({ slot }) {
  if (!slot) return <td className="px-3 py-2 text-center text-gray-400">-</td>

  const { file } = slot

  if (!file) {
    return (
      <td className="px-3 py-2 text-center">
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
          Belum upload
        </span>
      </td>
    )
  }

  if (file.status === 'pending') {
    return (
      <td className="px-3 py-2 text-center bg-yellow-50">
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
          Pending v{file.version}
        </span>
      </td>
    )
  }

  if (file.status === 'validated') {
    return (
      <td className="px-3 py-2 text-center bg-green-50">
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
          Valid v{file.version}
        </span>
      </td>
    )
  }

  if (file.status === 'rejected') {
    return (
      <td className="px-3 py-2 text-center bg-red-50" title={file.reject_reason || 'Ditolak'}>
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 cursor-help">
          Ditolak v{file.version}
        </span>
      </td>
    )
  }

  return <td className="px-3 py-2 text-center text-gray-400">-</td>
}

function FileRecapPage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    spkStatus: 'active',
    fileFilter: 'all',
  })

  useEffect(() => {
    fetchData()
  }, [filters])

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await fileService.getRecap(filters)
      if (res.success) {
        setData(res.data)
      }
    } catch (err) {
      console.error('Error fetching recap:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
  }

  const summary = data?.summary || { totalSlots: 0, belumUpload: 0, pending: 0, validated: 0, rejected: 0 }
  const spks = data?.spks || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Rekap File</h1>
        <p className="text-gray-500 text-sm mt-1">Pantau ketersediaan dan status file seluruh SPK</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Total Slot</p>
          <p className="text-2xl font-bold text-gray-800">{summary.totalSlots}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Belum Upload</p>
          <p className="text-2xl font-bold text-gray-500">{summary.belumUpload}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-400">
          <p className="text-sm text-gray-500">Pending Review</p>
          <p className="text-2xl font-bold text-yellow-600">{summary.pending}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-400">
          <p className="text-sm text-gray-500">Validated</p>
          <p className="text-2xl font-bold text-green-600">{summary.validated}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-400">
          <p className="text-sm text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{summary.rejected}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-4 items-center">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status SPK</label>
          <select
            value={filters.spkStatus}
            onChange={(e) => setFilters(f => ({ ...f, spkStatus: e.target.value }))}
            className="px-3 py-1.5 border rounded-lg text-sm"
          >
            <option value="active">Aktif (non-completed)</option>
            <option value="in_progress">In Progress saja</option>
            <option value="all">Semua</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Filter File</label>
          <select
            value={filters.fileFilter}
            onChange={(e) => setFilters(f => ({ ...f, fileFilter: e.target.value }))}
            className="px-3 py-1.5 border rounded-lg text-sm"
          >
            <option value="all">Semua</option>
            <option value="belum_upload">Belum Upload</option>
            <option value="pending">Pending Review</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="ml-auto text-sm text-gray-500">
          {spks.length} SPK ditampilkan
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="spinner w-8 h-8 mx-auto mb-3"></div>
            Memuat data...
          </div>
        ) : spks.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Tidak ada SPK yang sesuai filter
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-3 text-left font-medium text-gray-600">SPK</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Priority</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Deadline</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">ISI</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Tgl Valid ISI</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Cover</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Custom Covers</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Sisipan</th>
                  <th className="px-3 py-3 text-center font-medium text-gray-600">Kelengkapan</th>
                </tr>
              </thead>
              <tbody>
                {spks.map(spk => {
                  const isiSlot = spk.slots.find(s => s.type === 'isi')
                  const coverSlot = spk.slots.find(s => s.type === 'cover')
                  const customCovers = spk.customCoverSlots.filter(s => s.type === 'cover_custom')
                  const sisipanSlots = spk.customCoverSlots.filter(s => s.type === 'sisipan')
                  const { validated: val, total } = spk.kelengkapan
                  const progress = total > 0 ? Math.round((val / total) * 100) : 0

                  return (
                    <tr
                      key={spk.id}
                      className={`border-b hover:bg-gray-50 ${spk.highlight ? 'bg-yellow-50/50' : ''}`}
                    >
                      {/* SPK Name */}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => navigate(`/spk/${spk.id}`)}
                          className="text-left hover:text-blue-600 transition-colors"
                        >
                          <p className="font-medium text-gray-800">{spk.judul}</p>
                          <p className="text-xs text-gray-400">{spk.kode}</p>
                        </button>
                      </td>

                      {/* Priority */}
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium uppercase ${PRIORITY_COLORS[spk.priority] || PRIORITY_COLORS.normal}`}>
                          {spk.priority}
                        </span>
                      </td>

                      {/* Deadline */}
                      <td className="px-3 py-2 text-center text-gray-600">
                        {formatDate(spk.deadline)}
                      </td>

                      {/* ISI */}
                      <StatusCell slot={isiSlot} />

                      {/* Tgl Valid ISI */}
                      <td className="px-3 py-2 text-center text-xs text-gray-600">
                        {isiSlot?.file?.status === 'validated' && isiSlot.file.validated_at
                          ? new Date(isiSlot.file.validated_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: '2-digit' })
                          : <span className="text-gray-400">-</span>}
                      </td>

                      {/* Cover */}
                      <StatusCell slot={coverSlot} />

                      {/* Custom Covers */}
                      <td className="px-3 py-2">
                        {customCovers.length === 0 ? (
                          <span className="text-gray-400 text-center block">-</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 justify-center">
                            {customCovers.map(cc => {
                              let color = 'bg-gray-100 text-gray-500'
                              if (cc.file) {
                                if (cc.file.status === 'validated') color = 'bg-green-100 text-green-700'
                                else if (cc.file.status === 'pending') color = 'bg-yellow-100 text-yellow-700'
                                else if (cc.file.status === 'rejected') color = 'bg-red-100 text-red-700'
                              }
                              return (
                                <span
                                  key={cc.schoolCode}
                                  className={`inline-block px-1.5 py-0.5 rounded text-xs ${color}`}
                                  title={
                                    cc.file?.status === 'rejected'
                                      ? `${cc.schoolName}: ${cc.file.reject_reason || 'Ditolak'}`
                                      : `${cc.schoolName}: ${cc.file ? cc.file.status : 'Belum upload'}`
                                  }
                                >
                                  {cc.label}
                                </span>
                              )
                            })}
                          </div>
                        )}
                      </td>

                      {/* Sisipan */}
                      <td className="px-3 py-2">
                        {sisipanSlots.length === 0 ? (
                          <span className="text-gray-400 text-center block">-</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 justify-center">
                            {sisipanSlots.map(ss => {
                              let color = 'bg-gray-100 text-gray-500'
                              if (ss.file) {
                                if (ss.file.status === 'validated') color = 'bg-green-100 text-green-700'
                                else if (ss.file.status === 'pending') color = 'bg-yellow-100 text-yellow-700'
                                else if (ss.file.status === 'rejected') color = 'bg-red-100 text-red-700'
                              }
                              return (
                                <span
                                  key={ss.schoolCode}
                                  className={`inline-block px-1.5 py-0.5 rounded text-xs ${color}`}
                                  title={
                                    ss.file?.status === 'rejected'
                                      ? `${ss.schoolName}: ${ss.file.reject_reason || 'Ditolak'}`
                                      : `${ss.schoolName}: ${ss.file ? ss.file.status : 'Belum upload'}`
                                  }
                                >
                                  {ss.schoolCode}
                                </span>
                              )
                            })}
                          </div>
                        )}
                      </td>

                      {/* Kelengkapan */}
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${progress === 100 ? 'bg-green-500' : progress > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 whitespace-nowrap">
                            {val}/{total}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileRecapPage
